// Timeline view: one row per person, a bar running from their birth to their
// death - or to today, for the living.
//
// This started out as a dot per person on a generation lane, which was a
// mistake worth recording: a single point sitting in the past, on a grey band,
// reads as a memorial - "everyone is dead". A family timeline has to show
// living people as still going, so the mark is a SPAN, not an event at birth.
// A LIVING person's bar still fades toward its right end - "alive this long so
// far" - while a life that ended stops where it ended, squared off with a cap.
// Somebody gone whose date nobody has gets neither: no honest endpoint exists,
// so the bar is marked as open rather than guessed at.
//
// What it shows that the genealogy chart cannot: generational spacing, how the
// family's growth accelerates, and who is a contemporary of whom. The chart
// spaces people by lineage, so two cousins born a week apart sit far apart on
// it and read as unrelated in time.
import { birthOf, ageOf, deathOf, generations, generationsFromEgo, personOpenable, applyPersonLink } from "./genealogy"

const ROW_H = 22
const ROW_GAP = 2
const GROUP_HEAD = 22
const GROUP_GAP = 10
const PAD_X = 28
const PAD_TOP = 8
const AXIS_H = 26
const MIN_YEAR_PX = 6
const DOT_R = 3
const TICK_CLEARANCE = 34 // px: a decade label this close to "today" is dropped
const GUTTER_W = 196 // px: the fixed name column, never scrolled away from

function tickStep(years) {
    if (years <= 30) return 5
    if (years <= 80) return 10
    if (years <= 200) return 25
    return 50
}

export function renderTimeline(ctx) {
    let { nodes, parentsOf, partnersOf, linked, viewportWidth } = ctx
    let dated = []
    let undated = []
    nodes.forEach((node) => {
        let birth = birthOf(node)
        birth.known ? dated.push({ node, birth }) : undated.push(node)
    })

    let wrap = document.createElement("div")
    wrap.className = "ft-timeline"
    if (!dated.length) {
        wrap.appendChild(emptyState(undated.length))
        return { element: wrap, panZoom: false }
    }

    let today = new Date()
    // The same generation scale the chart lays out by, so a band here holds the
    // people on that row there. Counting for itself put a member's maternal
    // great-grandparents in "Oldest generation" while the chart had them eight
    // rows below its own oldest - two views disagreeing about the same family.
    let depth = generationsFromEgo({ egoId: ctx.egoId, parentsOf, childrenOf: ctx.childrenOf, partnersOf })
        || generations({ nodes, parentsOf, partnersOf })

    // Group by generation, but only for people the graph can actually place.
    // Somebody with no relationships at all has depth 0, which would file a
    // 2020 baby in the oldest generation purely because nobody has linked them
    // yet - so they get their own group instead of a wrong one.
    let groups = new Map()
    let loose = []
    dated.forEach((entry) => {
        if (!linked.has(entry.node.id)) {
            loose.push(entry)
            return
        }
        let generation = depth.get(entry.node.id) ?? 0
        if (!groups.has(generation)) groups.set(generation, [])
        groups.get(generation).push(entry)
    })

    let ordered = [...groups.keys()].sort((a, b) => a - b)
        .map((generation, index, all) => ({ key: `g${generation}`, label: laneLabel(index, all.length), rows: groups.get(generation) }))
    if (loose.length) ordered.push({ key: "loose", label: "Not linked to anyone yet", rows: loose, loose: true })

    // THE AXIS STOPS WHERE THE FAMILY DOES.
    //
    // It used to run to today no matter what, because the living need room to
    // the right. But a tree whose most recent death is 1923 then drew a century
    // of empty ruled paper after it, and at fit-zoom that dead space was most
    // of the view - every bar squeezed into the left third, unreadable, with
    // nothing beyond it. "Today" only earns its place when somebody is actually
    // still alive to reach it.
    let lastYear = dated.reduce((max, { node, birth }) => {
        let death = deathOf(node)
        if (!death.deceased) return today.getFullYear()
        return Math.max(max, death.known ? death.date.getFullYear() : birth.year)
    }, 0)
    let minYear = Math.min(...dated.map((d) => d.birth.year))
    let from = Math.floor(minYear / 5) * 5 - 5
    let to = Math.min(today.getFullYear() + 2, Math.ceil((lastYear + 3) / 5) * 5)
    let span = Math.max(to - from, 5)
    let livingReachToday = lastYear >= today.getFullYear()

    // The gutter is not part of the scrolling time area, so it comes off the
    // width before the scale is worked out.
    let usable = Math.max((viewportWidth || 900) - GUTTER_W - 2 * PAD_X, 320)
    let pxPerYear = Math.max(MIN_YEAR_PX, usable / span)
    let width = Math.round(span * pxPerYear) + 2 * PAD_X
    let xFor = (date) => {
        let year = date.getFullYear() + (date.getMonth() * 30 + date.getDate()) / 365
        return PAD_X + (year - from) * pxPerYear
    }
    let nowX = xFor(today)

    // ONE SCROLLER, NOT TWO.
    //
    // This used to report panZoom, which mounted it on the chart's transformed
    // stage: the whole timeline could be dragged and pinched like a canvas
    // while also being a list far taller than the screen. Two scroll models
    // answered the same gesture and the result felt broken - you pulled down
    // and the thing slid sideways. A timeline is a document. Vertical is the
    // page; horizontal belongs to the time area alone, and the name column and
    // the ruler stay put while it moves.
    let grid = document.createElement("div")
    grid.className = "ft-timeline-grid"
    let gutter = document.createElement("div")
    gutter.className = "ft-timeline-gutter"
    let scroller = document.createElement("div")
    scroller.className = "ft-timeline-scroller"

    let stage = document.createElement("div")
    stage.className = "ft-timeline-stage"
    stage.style.width = `${width}px`

    // Height first: the gridlines and the "now" line have to span every row.
    let height = PAD_TOP
    ordered.forEach((group) => {
        height += GROUP_HEAD + group.rows.length * (ROW_H + ROW_GAP) + GROUP_GAP
    })
    let bodyH = height

    // The ruler moves to the TOP and stays there. At the bottom of a
    // thousand-row list it was scrolled past on the first flick and never seen
    // again, which left every bar below it unmeasurable.
    let step = tickStep(span)
    let axis = document.createElement("div")
    axis.className = "ft-timeline-axis"
    axis.style.width = `${width}px`
    for (let year = Math.ceil(from / step) * step; year <= to; year += step) {
        let x = PAD_X + (year - from) * pxPerYear
        // The last decade label and "today" are usually within a few years of
        // each other, and two labels on the same spot render as one unreadable
        // smear ("202today"). "today" is the more useful of the two, so the
        // year yields.
        if (livingReachToday && Math.abs(x - nowX) < TICK_CLEARANCE) continue
        let tick = document.createElement("div")
        tick.className = "ft-timeline-tick"
        tick.style.left = `${x}px`
        tick.textContent = String(year)
        axis.appendChild(tick)
    }
    if (livingReachToday) {
        let nowTick = document.createElement("div")
        nowTick.className = "ft-timeline-tick ft-timeline-tick--now"
        nowTick.style.left = `${nowX}px`
        nowTick.textContent = "today"
        axis.appendChild(nowTick)
    }
    scroller.appendChild(axis)

    for (let year = Math.ceil(from / step) * step; year <= to; year += step) {
        let x = PAD_X + (year - from) * pxPerYear
        let rule = document.createElement("div")
        rule.className = "ft-timeline-rule"
        rule.style.left = `${x}px`
        rule.style.height = `${bodyH}px`
        stage.appendChild(rule)
    }

    if (livingReachToday) {
        let nowLine = document.createElement("div")
        nowLine.className = "ft-timeline-now"
        nowLine.style.left = `${nowX}px`
        nowLine.style.height = `${bodyH}px`
        stage.appendChild(nowLine)
    }

    let y = PAD_TOP
    ordered.forEach((group) => {
        let head = document.createElement("div")
        head.className = `ft-timeline-group${group.loose ? " ft-timeline-group--loose" : ""}`
        head.style.top = `${y}px`
        head.textContent = `${group.label} · ${group.rows.length}`
        gutter.appendChild(head)
        y += GROUP_HEAD

        group.rows.slice().sort((a, b) => a.birth.date - b.birth.date).forEach((entry) => {
            gutter.appendChild(labelRow(entry, y, today))
            stage.appendChild(row(entry, y, xFor, nowX, today))
            y += ROW_H + ROW_GAP
        })
        y += GROUP_GAP
    })

    gutter.style.height = `${bodyH}px`
    stage.style.height = `${bodyH}px`
    scroller.appendChild(stage)
    grid.appendChild(gutter)
    grid.appendChild(scroller)
    wrap.appendChild(grid)
    if (undated.length) wrap.appendChild(undatedRow(undated))
    return { element: wrap, panZoom: false }
}

// The name column. Carries the SAME data-node-id as its bar, so the search
// highlight and the dimming the controller applies by id light up both halves
// of the row rather than only the half that happens to be on screen.
//
// Names used to sit next to the bar, which meant they drifted off the left edge
// the moment you scrolled into the twentieth century - a wall of anonymous bars
// with the labels a thousand pixels behind you. Here they cannot leave.
function labelRow({ node, birth }, top, today) {
    let el = document.createElement(personOpenable(node) ? "a" : "div")
    el.className = "ft-timeline-row ft-timeline-row--label"
    el.dataset.nodeId = node.id
    el.style.top = `${top}px`
    if (personOpenable(node)) applyPersonLink(el, node)

    let name = document.createElement("span")
    name.className = "ft-timeline-name"
    name.textContent = node.name
    el.appendChild(name)

    let death = deathOf(node)
    let years = document.createElement("span")
    years.className = "ft-timeline-years"
    // A dash with nothing after it is how a genealogist writes "still living",
    // and it is also what an unknown death date looks like - so the living get
    // the open dash and somebody gone without a date gets an explicit mark.
    years.textContent = death.known
        ? `${birth.year}–${death.date.getFullYear()}`
        : (death.deceased ? `${birth.year}–?` : `${birth.year}–`)
    el.appendChild(years)
    return el
}

// Position in the tree, and nothing more.
//
// These used to read "Oldest generation / Their children / Grandchildren",
// which asserts DESCENT the bands do not have. A tree has more than one line in
// it - your father's parents and your mother's parents are both in the second
// band and are not one another's children - so "their children" was plainly
// false as soon as both sides were entered. Ordinals say the same useful thing
// (how far down) without claiming who came from whom, and they keep working
// past the fifth band instead of falling back to a different vocabulary.
const ORDINALS = ["Oldest generation", "Second generation", "Third generation", "Fourth generation", "Fifth generation", "Sixth generation", "Seventh generation", "Eighth generation"]

function laneLabel(index, total) {
    if (total <= 1) return "Everyone"
    return ORDINALS[index] || `Generation ${index + 1}`
}

function row({ node, birth }, top, xFor, nowX, today) {
    let left = xFor(birth.date)
    // A life that ended stops where it ended. Only the living run to the right
    // edge and keep the fade that means "still going" - drawing a closed
    // lifespan as an open one was the whole reason this view read every
    // grandparent as a 104-year-old.
    let death = deathOf(node)
    let endX = death.known ? xFor(death.date) : nowX
    let width = Math.max(endX - left, 4)
    let el = document.createElement(personOpenable(node) ? "a" : "div")
    el.className = "ft-timeline-row"
    el.dataset.nodeId = node.id
    el.style.top = `${top}px`
    if (personOpenable(node)) applyPersonLink(el, node)

    let bar = document.createElement("span")
    // --ended squares off the fade and caps the bar; --open marks somebody gone
    // whose date nobody has, where there is no honest endpoint to draw. That
    // second one is a question for the family, not a guess for us to make.
    bar.className = `ft-timeline-bar${death.known ? " ft-timeline-bar--ended" : (death.deceased ? " ft-timeline-bar--open" : "")}`
    bar.style.left = `${left}px`
    bar.style.width = `${width}px`
    el.appendChild(bar)

    if (death.known) {
        let cap = document.createElement("span")
        cap.className = "ft-timeline-end"
        cap.style.left = `${endX - DOT_R}px`
        el.appendChild(cap)
    }

    let dot = document.createElement("span")
    dot.className = "ft-timeline-dot"
    dot.style.left = `${left - DOT_R}px`
    el.appendChild(dot)

    // No name here any more - it lives in the gutter, where scrolling the
    // centuries cannot take it away. See labelRow.
    let when = birth.yearOnly ? String(birth.year) : birth.date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    // The age only earns its place for somebody gone - "68 years ago" already
    // says it for the living, and repeating it read as "68 years ago · 68".
    let age = ageOf(node, today)
    let parts = [node.name, `born ${when}`]
    if (node.relation) parts.push(node.relation)
    if (!death.deceased) {
        parts.push(`${today.getFullYear() - birth.year} years ago`)
    } else if (death.known) {
        let on = death.yearOnly ? death.year : death.date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
        parts.push(`died ${on}`)
        if (age != null) parts.push(`aged ${age}`)
    } else {
        parts.push("no longer living, date unknown")
    }
    el.title = parts.join(" · ")
    return el
}

// Day-and-month-only birthdays land here too (Contact::BIRTHDAY_NO_YEAR): the
// date is real, the year isn't, so there is no point on the axis to put them.
function undatedRow(nodes) {
    let row = document.createElement("div")
    row.className = "ft-timeline-undated"
    let title = document.createElement("div")
    title.className = "ft-timeline-undated-title"
    title.textContent = `No birth year yet (${nodes.length})`
    row.appendChild(title)
    let list = document.createElement("div")
    list.className = "ft-timeline-undated-list"
    nodes.slice().sort((a, b) => a.name.localeCompare(b.name)).forEach((node) => {
        let el = document.createElement(personOpenable(node) ? "a" : "span")
        el.className = "ft-timeline-chip"
        el.dataset.nodeId = node.id
        if (personOpenable(node)) applyPersonLink(el, node)
        el.textContent = node.name
        list.appendChild(el)
    })
    row.appendChild(list)
    return row
}

function emptyState(undatedCount) {
    let box = document.createElement("div")
    box.className = "ft-view-empty"
    let h = document.createElement("h2")
    h.className = "ft-view-empty-title"
    h.textContent = "No birthdays yet"
    box.appendChild(h)
    let p = document.createElement("p")
    p.className = "text-muted small mb-0"
    p.textContent = undatedCount
        ? `The timeline runs each person from their birth to today, and none of these ${undatedCount} has a birth year yet. Add one from a contact's profile and they'll appear here.`
        : "The timeline runs each person from their birth to today. Add a birthday from a contact's profile to get started."
    box.appendChild(p)
    return box
}
